import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import ImageResize from 'tiptap-extension-resize-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { uploadImageToFirebase, isValidImage } from '../../utils/imageUpload';
import '../../theme/tiptap-editor.scss';

// Iconos SVG para los botones
const Icons = {
  Bold: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/>
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/>
    </svg>
  ),
  Italic: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 7V4a1 1 0 0 0-1-1H9a1 1 0 0 0 0 2h2.829l-3.333 10H6a1 1 0 0 0 0 2h9a1 1 0 0 0 0-2h-2.829l3.333-10H18a1 1 0 0 0 1-1z"/>
    </svg>
  ),
  Underline: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19v2h12v-2H6zM8 2v8.5a4 4 0 0 0 8 0V2h-2v8.5a2 2 0 0 1-4 0V2H8z"/>
    </svg>
  ),
  Strike: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.154 14c.23.516.346 1.09.346 1.72 0 1.342-.524 2.392-1.571 3.147C14.88 19.622 13.433 20 11.586 20c-1.64 0-3.263-.381-4.87-1.144V16.6c1.52.877 3.075 1.316 4.666 1.316 2.551 0 3.83-.732 3.83-2.197a2.61 2.61 0 0 0-.723-1.719H17.154zM11.586 4c1.362 0 2.491.328 3.387.985C15.869 5.64 16.316 6.514 16.316 7.6c0 .617-.137 1.2-.41 1.751h-2.155c.185-.48.277-.98.277-1.5 0-.906-.378-1.36-1.134-1.36-.706 0-1.059.454-1.059 1.36H9.680c0-1.085.447-1.959 1.342-2.616C11.918 4.328 12.948 4 14.31 4H11.586z"/>
      <path d="M3 12h18v2H3z"/>
    </svg>
  ),
  Code: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H8zm1 2h6v14H9V5z"/>
    </svg>
  ),
  H1: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4v6h6V4h2v16h-2v-6H6v6H4V4h2zm8 8h2v8h-2v-8z"/>
    </svg>
  ),
  H2: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4v6h6V4h2v16h-2v-6H6v6H4V4h2zm8 8h4v2h-4v-2z"/>
    </svg>
  ),
  H3: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4v6h6V4h2v16h-2v-6H6v6H4V4h2zm8 8h3v2h-3v-2z"/>
    </svg>
  ),
  BulletList: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="4" cy="7" r="1"/>
      <circle cx="4" cy="12" r="1"/>
      <circle cx="4" cy="17" r="1"/>
      <path d="M7 7h14v2H7zM7 12h14v2H7zM7 17h14v2H7z"/>
    </svg>
  ),
  OrderedList: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V6H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
    </svg>
  ),
  Quote: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
    </svg>
  ),
  Image: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
    </svg>
  ),
  Link: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
    </svg>
  ),
  Undo: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
    </svg>
  ),
  Redo: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>
    </svg>
  ),
  AlignLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 4h18v2H3V4zm0 4h12v2H3V8zm0 4h18v2H3v-2zm0 4h12v2H3v-2z"/>
    </svg>
  ),
  AlignCenter: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 4h18v2H3V4zm3 4h12v2H6V8zm-3 4h18v2H3v-2zm3 4h12v2H6v-2z"/>
    </svg>
  ),
  AlignRight: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 4h18v2H3V4zm6 4h12v2H9V8zm-6 4h18v2H3v-2zm6 4h12v2H9v-2z"/>
    </svg>
  ),
  AlignJustify: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 4h18v2H3V4zm0 4h18v2H3V8zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
    </svg>
  ),
};

const TextAlignment = Extension.create({
  name: 'textAlignment',

  addGlobalAttributes() {
    return [
      {
        types: ['heading', 'paragraph'],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: element => element.style.textAlign || null,
            renderHTML: attributes => {
              if (!attributes.textAlign) return {};
              return { style: `text-align: ${attributes.textAlign}` };
            },
          },
        },
      },
    ];
  },
});

const TiptapEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Escribe tu contenido aquí...', 
  className = '',
  disabled = false,
  showImageUpload = true,
  imageFolder = 'editor-images',
  columnCount = 1,
  showToolbar = true,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      TextAlignment,
      ImageResize.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
        addAttributes() {
          return {
            ...this.parent?.(),
            align: {
              default: 'none',
              parseHTML: element => element.getAttribute('data-align'),
              renderHTML: attributes => {
                if (!attributes.align) return {}
                return { 'data-align': attributes.align, class: `tiptap-image align-${attributes.align}` }
              },
            },
          }
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
      Underline.configure({
        HTMLAttributes: {
          class: 'tiptap-underline',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) {
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: `tiptap-editor ${columnCount === 2 || columnCount === '2' ? 'two-columns' : 'one-column'}`,
        'data-placeholder': placeholder,
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (isValidImage(file)) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event, slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find(item => item.type.indexOf('image') === 0);
        
        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file && isValidImage(file)) {
            handleImageUpload(file);
          }
          return true;
        }
        return false;
      },
    },
    editable: !disabled,
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;

    const editorElement = editor.view.dom;
    const shouldUseTwoColumns = columnCount === 2 || columnCount === '2';

    editorElement.classList.toggle('two-columns', shouldUseTwoColumns);
    editorElement.classList.toggle('one-column', !shouldUseTwoColumns);
    editorElement.setAttribute('data-placeholder', placeholder);
  }, [columnCount, editor, placeholder]);

  const handleImageUpload = async (file) => {
    if (!editor || !file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const imageUrl = await uploadImageToFirebase(file, imageFolder, (progress) => {
        setUploadProgress(progress);
      });

      editor.chain().focus().setImage({ src: imageUrl, alt: file.name }).run();
    } catch (error) {
      console.error('Error al subir imagen:', error);
      alert('Error al subir la imagen: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file && isValidImage(file)) {
      handleImageUpload(file);
    } else if (file) {
      alert('Por favor selecciona una imagen válida (JPG, PNG, GIF, WebP) menor a 5MB');
    }
    // Limpiar el input
    event.target.value = '';
  };

  const addLink = () => {
    if (!editor) return;
    
    const url = window.prompt('Ingresa la URL del enlace:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const removeLink = () => {
    if (editor) {
      editor.chain().focus().unsetLink().run();
    }
  };

  const setTextAlign = (textAlign) => {
    if (!editor) return;

    const nodeType = editor.isActive('heading') ? 'heading' : 'paragraph';
    editor.chain().focus().updateAttributes(nodeType, { textAlign }).run();
  };

  const isTextAlignActive = (textAlign) => (
    editor.isActive('paragraph', { textAlign }) || editor.isActive('heading', { textAlign })
  );

  if (!editor) {
    return <div className="tiptap-editor-loading">Cargando editor...</div>;
  }

  return (
    <div className={`tiptap-wrapper ${className} ${isUploading ? 'tiptap-editor-loading' : ''}`}>
      {/* Barra de herramientas */}
      {showToolbar && (
        <div className="tiptap-toolbar">
          {/* Formato de texto */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            data-tooltip="Negrita"
          >
            <Icons.Bold />
          </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          data-tooltip="Cursiva"
        >
          <Icons.Italic />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          data-tooltip="Subrayado"
        >
          <Icons.Underline />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          data-tooltip="Tachado"
        >
          <Icons.Strike />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          className={editor.isActive('code') ? 'is-active' : ''}
          data-tooltip="Código"
        >
          <Icons.Code />
        </button>

        <div className="toolbar-separator"></div>

        {/* Encabezados */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          data-tooltip="Título 1"
        >
          <Icons.H1 />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          data-tooltip="Título 2"
        >
          <Icons.H2 />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          data-tooltip="Título 3"
        >
          <Icons.H3 />
        </button>

        <div className="toolbar-separator"></div>

        {/* Alineacion de texto */}
        <button
          type="button"
          onClick={() => setTextAlign('left')}
          className={isTextAlignActive('left') ? 'is-active' : ''}
          data-tooltip="Alinear texto a la izquierda"
        >
          <Icons.AlignLeft />
        </button>

        <button
          type="button"
          onClick={() => setTextAlign('center')}
          className={isTextAlignActive('center') ? 'is-active' : ''}
          data-tooltip="Centrar texto"
        >
          <Icons.AlignCenter />
        </button>

        <button
          type="button"
          onClick={() => setTextAlign('right')}
          className={isTextAlignActive('right') ? 'is-active' : ''}
          data-tooltip="Alinear texto a la derecha"
        >
          <Icons.AlignRight />
        </button>

        <button
          type="button"
          onClick={() => setTextAlign('justify')}
          className={isTextAlignActive('justify') ? 'is-active' : ''}
          data-tooltip="Justificar texto"
        >
          <Icons.AlignJustify />
        </button>

        <div className="toolbar-separator"></div>

        {/* Listas */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          data-tooltip="Lista con viñetas"
        >
          <Icons.BulletList />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          data-tooltip="Lista numerada"
        >
          <Icons.OrderedList />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
          data-tooltip="Cita"
        >
          <Icons.Quote />
        </button>

        <div className="toolbar-separator"></div>

        {/* Enlaces e imágenes */}
        {showImageUpload && (
          <button
            type="button"
            onClick={handleImageButtonClick}
            disabled={isUploading}
            data-tooltip="Insertar imagen"
          >
            <Icons.Image />
          </button>
        )}

        <button
          type="button"
          onClick={editor.isActive('link') ? removeLink : addLink}
          className={editor.isActive('link') ? 'is-active' : ''}
          data-tooltip={editor.isActive('link') ? 'Quitar enlace' : 'Insertar enlace'}
        >
          <Icons.Link />
        </button>

        {/* Controles de alineación de imagen */}
        {editor.isActive('image') && (
          <>
            <div className="toolbar-separator"></div>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()}
              className={editor.getAttributes('image').align === 'left' ? 'is-active' : ''}
              data-tooltip="Alinear imagen a la izquierda"
            >
              <Icons.AlignLeft />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()}
              className={editor.getAttributes('image').align === 'center' ? 'is-active' : ''}
              data-tooltip="Centrar imagen"
            >
              <Icons.AlignCenter />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()}
              className={editor.getAttributes('image').align === 'right' ? 'is-active' : ''}
              data-tooltip="Alinear imagen a la derecha"
            >
              <Icons.AlignRight />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { align: 'none' }).run()}
              className={editor.getAttributes('image').align === 'none' || !editor.getAttributes('image').align ? 'is-active' : ''}
              data-tooltip="Sin alineación"
            >
              <Icons.AlignJustify />
            </button>
          </>
        )}

        <div className="toolbar-separator"></div>

        {/* Deshacer/Rehacer */}
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          data-tooltip="Deshacer"
        >
          <Icons.Undo />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          data-tooltip="Rehacer"
        >
          <Icons.Redo />
        </button>
      </div>
      )}

      {/* Área de edición */}
      <EditorContent editor={editor} />

      {/* Input oculto para seleccionar archivos */}
      {showImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      )}

      {/* Indicador de progreso de subida */}
      {isUploading && (
        <div className="upload-progress">
          <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
          <span className="upload-progress-text">Subiendo imagen... {Math.round(uploadProgress)}%</span>
        </div>
      )}
    </div>
  );
};

export default TiptapEditor;
